import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export const languages = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "zh", name: "中文", flag: "🇨🇳" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "hi", name: "हिंदी", flag: "🇮🇳" },
  { code: "kn", name: "ಕನ್ನಡ", flag: "🇮🇳" },
  { code: "ud", name: "User Defined", flag: "🔄" },
];

export const tripDurationOptions = [
  { value: 30, label: "30 Minutes" },
  { value: 45, label: "45 Minutes" },
  { value: 60, label: "1 Hour" },
  { value: 90, label: "1.5 Hours" },
  { value: 120, label: "2 Hours" },
  { value: 180, label: "3 Hours" },
  { value: 240, label: "4 Hours" },
  { value: 360, label: "6 Hours" },
  { value: 480, label: "8 Hours" },
  { value: 720, label: "12 Hours" },
  { value: 1440, label: "24 Hours" },
];

// Average stop time configuration options (in minutes)
export const averageStopTimeOptions = [
  { value: 0.5, label: "30 seconds" },
  { value: 1, label: "1 minute" },
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
];

// Arriving status distance options (in meters)
export const arrivingStatusDistanceOptions = [
  { value: 50, label: "50 meters" },
  { value: 100, label: "100 meters" },
  { value: 150, label: "150 meters" },
  { value: 200, label: "200 meters" },
  { value: 300, label: "300 meters" },
  { value: 500, label: "500 meters" },
  { value: 750, label: "750 meters" },
  { value: 1000, label: "1 km" },
];

export const distanceThresholdOptions = [
  { value: 50, label: "50 meters" },
  { value: 100, label: "100 meters" },
  { value: 150, label: "150 meters" },
  { value: 200, label: "200 meters" },
  { value: 300, label: "300 meters" },
  { value: 500, label: "500 meters" },
  { value: 750, label: "750 meters" },
  { value: 1000, label: "1 km" },
];

export const translateSettingOptionLabel = (label, t) => {
  if (label === null || label === undefined) return "";
  const text = String(label);
  const unitMatch = text.match(
    /^(\d+(?:\.\d+)?)\s*(seconds?|minutes?|hours?|days?|meters?|kilometers?|m|km|hr)$/i,
  );

  if (!unitMatch) {
    return t(text);
  }

  const [, amount, unit] = unitMatch;
  const unitKey = unit.toLowerCase();
  return `${amount} ${t(unitKey)}`;
};

export const SettingRow = ({
  label,
  name,
  value,
  onChange,
  options,
  disabled,
}) => {
  const { t } = useTranslation();

  return (
    <Box className="setting-row">
      <Typography className="form-label">{label}</Typography>
      <Box sx={{ flex: 1 }}>
        <FormControl fullWidth>
          <Select
            name={name}
            value={value || ""}
            onChange={onChange}
            className="custom-select"
            displayEmpty
            disabled={disabled}
            sx={{
              height: "48px",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E0E0E0",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#A5DCF3",
              },
              backgroundColor: "#FFFFFF",
            }}
          >
            {Array.isArray(options) && options[0]?.code
              ? options.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {lang.flag && <span>{lang.flag}</span>}
                      <span>{translateSettingOptionLabel(lang.name, t)}</span>
                    </Box>
                  </MenuItem>
                ))
              : options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {translateSettingOptionLabel(opt, t)}
                  </MenuItem>
                ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export const SettingTextRow = ({
  label,
  name,
  value,
  onChange,
  onBlur, // 👈 add
  disabled,
  type = "text",
  min,
}) => (
  <Box className="setting-row">
    <Typography className="form-label">{label}</Typography>
    <Box sx={{ flex: 1 }}>
      <TextField
        name={name}
        value={value ?? ""}
        onChange={onChange}
        onBlur={onBlur} // 👈 add
        fullWidth
        type={type}
        disabled={disabled}
        inputProps={type === "number" ? { min: min ?? 0 } : {}}
        onKeyDown={
          type === "number"
            ? (e) => {
                if (e.key === "-" || e.key === "e") e.preventDefault();
              }
            : undefined
        }
        sx={{
          "& .MuiOutlinedInput-root": { height: "48px", borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#A5DCF3",
          },
          backgroundColor: "#FFFFFF",
        }}
      />
    </Box>
  </Box>
);

export const SettingRowSkeleton = () => (
  <Box className="setting-row">
    <Skeleton width={200} height={40} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" height={40} sx={{ borderRadius: "4px" }} />
  </Box>
);

export const isValidMapViewLayerCombination = (mapView, mapLayer) => {
  const invalidCombinations = {
    Satellite: ["Traffic", "Transit", "Bicycling"],
    Terrain: ["Traffic", "Transit"],
    Hybrid: ["Traffic", "Transit", "Bicycling"],
  };

  return !invalidCombinations[mapView]?.includes(mapLayer);
};

export const formatMapLayer = (mapLayer) => {
  if (!mapLayer) return "";
  return mapLayer
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
